/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListDirectoryPeopleRequest,
  ListDirectoryPeopleSecurity,
} from "../models/operations/listdirectorypeople.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildDirectoryPeopleQuery,
  DirectoryPeopleQueryData,
  prefetchDirectoryPeople,
  queryKeyDirectoryPeople,
} from "./directoryPeople.core.js";
export {
  buildDirectoryPeopleQuery,
  type DirectoryPeopleQueryData,
  prefetchDirectoryPeople,
  queryKeyDirectoryPeople,
};

export type DirectoryPeopleQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listDirectoryPeople access
 *
 * @remarks
 * List every live directory profile in the active organization with its name, groups and whether it belongs to a member. Requires an authenticated session authorized for org:admin, because it names people who are not members; API keys cannot enumerate it. Attribute values are not returned.
 */
export function useDirectoryPeople(
  request?: ListDirectoryPeopleRequest | undefined,
  security?: ListDirectoryPeopleSecurity | undefined,
  options?: QueryHookOptions<
    DirectoryPeopleQueryData,
    DirectoryPeopleQueryError
  >,
): UseQueryResult<DirectoryPeopleQueryData, DirectoryPeopleQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildDirectoryPeopleQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listDirectoryPeople access
 *
 * @remarks
 * List every live directory profile in the active organization with its name, groups and whether it belongs to a member. Requires an authenticated session authorized for org:admin, because it names people who are not members; API keys cannot enumerate it. Attribute values are not returned.
 */
export function useDirectoryPeopleSuspense(
  request?: ListDirectoryPeopleRequest | undefined,
  security?: ListDirectoryPeopleSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    DirectoryPeopleQueryData,
    DirectoryPeopleQueryError
  >,
): UseSuspenseQueryResult<DirectoryPeopleQueryData, DirectoryPeopleQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildDirectoryPeopleQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setDirectoryPeopleData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      limit?: number | undefined;
      cursor?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: DirectoryPeopleQueryData,
): DirectoryPeopleQueryData | undefined {
  const key = queryKeyDirectoryPeople(...queryKeyBase);

  return client.setQueryData<DirectoryPeopleQueryData>(key, data);
}

export function invalidateDirectoryPeople(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      limit?: number | undefined;
      cursor?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "access",
      "listDirectoryPeople",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllDirectoryPeople(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "access", "listDirectoryPeople"],
  });
}
